import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axios";
import { FaShieldAlt, FaSpinner, FaTimesCircle, FaCheckCircle, FaUserPlus } from "react-icons/fa";
import Navbar from "../../components/companyLandingComponents/Navbar/Navbar";
import BottomFooter from "../../components/Footer/BottomFooter/BottomFooter";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

// Client-side defense only; the backend enforces the real redirect allowlist.
const parseSafeRedirectUri = (value) => {
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol === "https:") return url;
  if (url.protocol === "http:" && LOCAL_HOSTS.has(url.hostname)) return url;
  return null;
};

const SSOAuthorize = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get("client_id");
  const redirectUri = searchParams.get("redirect_uri");
  const state = searchParams.get("state") || "";

  const [status, setStatus] = useState("loading"); // loading, success, error_no_role, error_invalid
  const [errorMessage, setErrorMessage] = useState("");
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    // 1. Validate parameters
    if (!clientId || !redirectUri) {
      setStatus("error_invalid");
      setErrorMessage("Missing required SSO parameters (client_id or redirect_uri).");
      return;
    }

    const safeRedirect = parseSafeRedirectUri(redirectUri);
    if (!safeRedirect) {
      setStatus("error_invalid");
      setErrorMessage("Invalid redirect_uri: it must be an https:// URL.");
      return;
    }

    // 2. Check if logged in
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (!token || !user) {
      // Preserve query params to redirect back after login
      const currentParams = searchParams.toString();
      navigate(`/login?redirect=/sso/authorize&${currentParams}`, { replace: true });
      return;
    }

    // 3. Initiate authorization with Mother Company backend
    const performSSOAuthorize = async () => {
      try {
        const response = await api.post("/api/sso/authorize", {
          client_id: parseInt(clientId, 10),
          redirect_uri: redirectUri,
        });

        if (response.data?.code) {
          setStatus("success");
          // Redirect the browser back to client website
          const finalUrl = new URL(safeRedirect.toString());
          finalUrl.searchParams.set("code", response.data.code);
          finalUrl.searchParams.set("state", state);

          setTimeout(() => {
            window.location.href = finalUrl.toString();
          }, 1500); // Small delay to let user see success state
        } else {
          throw new Error("Failed to receive authorization code.");
        }
      } catch (err) {
        const statusResponse = err.response?.status;
        const msg = err.response?.data?.error || err.message || "An unexpected error occurred.";

        if (statusResponse === 403) {
          setStatus("error_no_role");
          // Try to fetch company details if possible to show user-friendly message
          try {
            // Reason: companies-crud is admin-only; the shared list is open to any user.
            const compRes = await api.get("/api/companies");
            const match = (compRes.data || []).find((c) => String(c.id) === String(clientId));
            setCompanyName(match?.name || `Company #${clientId}`);
          } catch (e) {
            setCompanyName(`Company #${clientId}`);
          }
        } else {
          setStatus("error_invalid");
          setErrorMessage(msg);
        }
      }
    };

    performSSOAuthorize();
  }, [clientId, redirectUri, state, navigate, searchParams]);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa", display: "flex", flexDirection: "column" }}>
      <Navbar />

      {/* Background image overlay matching login */}
      <div
        className="position-fixed start-0 end-0 top-0 bottom-0"
        style={{
          background: 'url("/assets/images/iconicback.jpg") center/cover no-repeat',
          zIndex: 0,
          opacity: 0.85
        }}
      ></div>

      <div
        className="container position-relative d-flex align-items-center justify-content-center flex-grow-1"
        style={{ zIndex: 1, minHeight: "calc(100vh - 160px)", padding: "40px 15px" }}
      >
        <div 
          className="card border-0 shadow-lg p-5 text-center" 
          style={{ 
            maxWidth: "500px", 
            width: "100%", 
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(10px)"
          }}
        >
          {/* Brand Logo */}
          <div className="mb-4">
            <img
              src="/assets/images/logo.png"
              alt="logo"
              style={{ maxWidth: "150px" }}
            />
          </div>

          {/* LOADING STATE */}
          {status === "loading" && (
            <div>
              <div className="mb-4">
                <FaSpinner className="text-primary spin-animation" size={50} />
              </div>
              <h3 className="fw-bold text-dark mb-2">Authorizing Application</h3>
              <p className="text-muted">
                Please wait while we establish a secure connection with the sister company...
              </p>
            </div>
          )}

          {/* SUCCESS STATE */}
          {status === "success" && (
            <div>
              <div className="mb-4 text-success animate-bounce">
                <FaCheckCircle size={50} />
              </div>
              <h3 className="fw-bold text-dark mb-2">Authorization Granted</h3>
              <p className="text-muted">
                Redirecting you back to the client application safely...
              </p>
              <div className="progress mt-3" style={{ height: "4px" }}>
                <div 
                  className="progress-bar bg-success progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: "100%" }}
                ></div>
              </div>
            </div>
          )}

          {/* NO ROLE IN COMPANY STATE */}
          {status === "error_no_role" && (
            <div>
              <div className="mb-4 text-warning">
                <FaShieldAlt size={50} />
              </div>
              <h3 className="fw-bold text-dark mb-2">Access Denied</h3>
              <p className="text-danger mb-4 fw-semibold">
                You do not have an approved role in <strong>{companyName || `Company #${clientId}`}</strong>.
              </p>
              <p className="text-muted small mb-4">
                Single Sign-On requires you to hold a verified role in the requesting company. 
                Would you like to apply for a role?
              </p>
              <div className="d-grid gap-2">
                <button 
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2 py-2 fw-bold"
                  onClick={() => navigate("/role_selection")}
                  style={{ borderRadius: "8px" }}
                >
                  <FaUserPlus /> Request Role for {companyName || "Company"}
                </button>
                <button 
                  className="btn btn-outline-secondary py-2"
                  onClick={() => navigate("/profile")}
                  style={{ borderRadius: "8px" }}
                >
                  Go to Profile
                </button>
              </div>
            </div>
          )}

          {/* GENERAL ERROR STATE */}
          {status === "error_invalid" && (
            <div>
              <div className="mb-4 text-danger animate-pulse">
                <FaTimesCircle size={50} />
              </div>
              <h3 className="fw-bold text-dark mb-2">SSO Error</h3>
              <p className="text-danger mb-4 fw-semibold">{errorMessage}</p>
              <p className="text-muted small mb-4">
                Please contact support if you believe this is a technical mistake.
              </p>
              <div className="d-grid">
                <button 
                  className="btn btn-secondary py-2"
                  onClick={() => navigate("/")}
                  style={{ borderRadius: "8px" }}
                >
                  Back to Homepage
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomFooter />

      {/* Simple animations injection */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin-animation {
          animation: spin 1.5s linear infinite;
        }
        .animate-bounce {
          animation: bounce 1s infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-pulse {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default SSOAuthorize;
